import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react'
import Input from 'core/components/inputs/Input'
import useCurrentTeam from 'core/hooks/useCurrentTeam'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import APIService from 'api/APIService'
import { ReportTiny, TransactionDetailed, TransactionShort } from 'types/entity'
import useNotifications from 'core/hooks/useNotifications'
import Button from 'core/components/buttons/Button'
import FilePicker from 'core/widgets/transaction-detail/FilePicker'
import { Controller, useForm } from 'react-hook-form'
import SmartField from 'core/components/form/SmartField'
import { LockFilledIcon } from 'core/_legacy/icons'
import Select from 'core/components/inputs/Select'
import usePurposes from 'core/hooks/usePurposes'
import { getUniqueListBy } from 'utils/AppHelper'
import Status from 'core/widgets/status/Status'
import useRolesAndPermissions from 'core/hooks/useRolesAndPermissions'
import Field from 'core/components/form/Field'
import moment from 'moment'
import Autosuggest from 'core/components/inputs/autosuggest/Autosuggest'
import CategoryOption from 'core/widgets/transaction-detail/CategoryOption'
import cn from 'classnames'
import numeral from 'numeral'
import PopConfirm from 'core/components/dialogs/PopConfirm'
import DatePicker from 'core/components/inputs/date-picker/DatePicker'
import useNavigation from 'core/hooks/useNavigation'
import Tooltip from 'core/components/dialogs/Tooltip'
import Icon from 'core/components/icons/Icon'
import { MenuProps } from 'core/components/dialogs/Menu'

interface TransactionDetailProps {
  view: 'personal' | 'team'
  transactionId: TransactionDetailed['token_id']
  onError?: VoidFunction
  onSuccess?: (t?: TransactionShort) => void
  readOnly?: boolean // True by default - will be shown as a first stage only on pay periods
  enableStartDate?: string | null
  enableEndDate?: string | null
}

interface FormValues {
  amount: string
  purpose: TransactionDetailed['purpose']
  merchant: TransactionDetailed['merchant']
  date: TransactionDetailed['date']
  notes: TransactionDetailed['notes']
  category: TransactionDetailed['category']
  tags: TransactionDetailed['tags']
  report_token_id: ReportTiny['token_id'] | Partial<ReportTiny> | null
  photo?: TransactionDetailed['photo']
  photo_attributes: File | null
}

export interface TransactionDetailRef {
  getFormDirty: () => boolean
}

const TransactionDetail = forwardRef(
  (
    {
      view,
      transactionId,
      onError,
      onSuccess,
      readOnly = false,
      enableStartDate,
      enableEndDate
    }: TransactionDetailProps,
    ref: Ref<TransactionDetailRef>
  ) => {
    const notifications = useNotifications()
    const { pathname } = useNavigation()
    const isPersonalReportPage =
      pathname.includes('reports') && !pathname.includes('team')
    const reportId = useMemo(() => {
      return pathname?.split('/')?.[2] ?? ''
    }, [pathname])

    const { data: team } = useCurrentTeam()
    const { categories_enabled, tags_enabled } = team ?? {}
    const queryClient = useQueryClient()
    const { allPurposes, teamPurposes, customIncomeSources } = usePurposes()
    const { hasTeam } = useRolesAndPermissions()
    const [tagQuery, setTagQuery] = useState('')
    const isNewTransaction = useMemo(
      () => String(transactionId).startsWith('new-id-'),
      [transactionId]
    )
    const {
      control,
      reset,
      setValue,
      watch,
      setError,
      formState: { isDirty },
      handleSubmit
    } = useForm<FormValues>({
      defaultValues: {
        amount: '$0.00',
        report_token_id:
          isNewTransaction && isPersonalReportPage ? reportId : undefined
      }
    })

    // Transaction detail drawer uses it to get the form state
    useImperativeHandle(
      ref,
      () => ({
        getFormDirty: () => isDirty
      }),
      [isDirty]
    )

    const { date, amount: formAmount, photo, report_token_id } = watch()
    const amount = formAmount ?? 0
    const amountNumber = formAmount
      ? parseFloat(formAmount?.replaceAll(/(\$|,)/g, ''))
      : 0

    const queryKey = useMemo(() => {
      return view === 'personal'
        ? ['transaction', 'detail', transactionId]
        : ['team', team?.token_id, 'transaction', transactionId]
    }, [team?.token_id, transactionId, view])

    const { data: transaction, isLoading } = useQuery(
      queryKey,
      () => {
        return view === 'team'
          ? APIService.transactions.getReportTransaction({
              transactionId: transactionId,
              teamId: team?.token_id ?? ''
            })
          : APIService.transactions.getTransaction({
              transactionId: transactionId
            })
      },
      {
        enabled:
          view === 'team'
            ? !!(transactionId && team?.token_id)
            : !!transactionId && !isNewTransaction,
        retry: false,
        onError: () => {
          onError?.()
          notifications.error(
            'Could not fetch transaction detail. Please try again later.'
          )
        }
      }
    )

    const isReadOnly = readOnly || !!transaction?.is_locked || view === 'team'

    const { data: tagsData, isFetching: isLoadingTags } = useQuery(
      ['tags', { query: tagQuery }],
      () => APIService.tags.getTags({ q: tagQuery, page: 1, page_size: 100 }),
      {
        refetchOnWindowFocus: false
      }
    )

    const { data: tags } = tagsData ?? {}

    const { data: reports } = useQuery(
      ['reports', { status: 'draft' }],
      () =>
        APIService.reports.getReports({
          limit: 50
        }),
      {
        enabled: !!transactionId,
        refetchOnWindowFocus: false
      }
    )

    const categoryInputConfig = useMemo((): {
      show: boolean
      disabled: boolean
    } => {
      if (!hasTeam) {
        return {
          show: true,
          disabled: false
        }
      }

      return {
        show: categories_enabled ? true : !!transaction?.category,
        disabled: categories_enabled ? false : !!transaction?.category
      }
    }, [categories_enabled, hasTeam, transaction?.category])

    const tagsInputConfig = useMemo((): {
      show: boolean
      disabled: boolean
    } => {
      if (!hasTeam) {
        return {
          show: true,
          disabled: false
        }
      }

      return {
        show: tags_enabled ? true : !!transaction?.tags.length,
        disabled: tags_enabled ? false : !!transaction?.tags?.length
      }
    }, [hasTeam, tags_enabled, transaction?.tags?.length])

    const filteredReports = useMemo(() => {
      return (
        reports?.filter((data) => {
          const { period_start_date, period_end_date, token_id, status } = data
          if ((!period_start_date || !period_end_date) && status === 'draft')
            return true
          if (
            !period_start_date ||
            !period_end_date ||
            !['draft', 'paid'].includes(status)
          )
            return false
          const periodStart = moment(period_start_date, 'M/D/YYYY')
          const periodEnd = moment(period_end_date, 'M/D/YYYY')
          return (
            moment(date).isBetween(periodStart, periodEnd, 'days', '[]') ||
            report_token_id === token_id
          )
        }) ?? []
      )
    }, [reports, report_token_id, date])

    const { isLoading: updatingTransaction, mutate: updateTransaction } =
      useMutation(
        (formValues: FormData) => {
          return APIService.transactions.updateTransaction(
            transactionId,
            formValues,
            {
              'Content-Type': 'multipart/form-data'
            }
          )
        },
        {
          onSuccess(data) {
            if (isPersonalReportPage && data.report?.token_id === reportId)
              queryClient.invalidateQueries(['reports', reportId])
            notifications.success(
              `${isExpense ? 'Expense' : 'Revenue'} has been updated successfully`
            )
            setValue('photo', data.photo)
            onSuccess?.(data)
          }
        }
      )

    const { isLoading: creatingTransaction, mutate: createTransaction } =
      useMutation(
        (formValues: FormData) => {
          return APIService.transactions.createTransaction(formValues, {
            'Content-Type': 'multipart/form-data'
          })
        },
        {
          onSuccess(data) {
            setValue('photo', data.photo)
            if (isPersonalReportPage && data.report?.token_id === reportId)
              queryClient.invalidateQueries(['reports', reportId])
            onSuccess?.(data)
            notifications.success(
              `${isExpense ? 'Expense' : 'Revenue'} has been created successfully`
            )
          },
          onError({ response }) {
            Object.keys(response.data).forEach((key) => {
              setError(key as keyof FormValues, {
                message: response.data[key][0]
              })
            })
          }
        }
      )

    const { mutate: removeFromReport } = useMutation(
      ['reports-detach', 'transactions'],
      (transactionId: TransactionShort['token_id']) =>
        APIService.reports.detachFromReport({
          reportId: String(
            transaction?.report?.token_id
          ) as ReportTiny['token_id'],
          kind: 'transactions',
          id: transactionId
        })
    )

    const { isLoading: deletingTransaction, mutate: deleteTransaction } =
      useMutation(APIService.transactions.batchDeleteTransactions, {
        onSuccess: () => {
          if (isPersonalReportPage)
            queryClient.invalidateQueries(['reports', reportId])
          notifications.success(
            `${isExpense ? 'Expense' : 'Revenue'} has been deleted successfully`
          )
          onSuccess?.()
        },
        onError: (err) => {
          console.error(err)
          notifications.error('Could not delete transaction')
        }
      })

    const onDelete = useCallback(() => {
      removeFromReport(transactionId)
      deleteTransaction({
        token_ids: [transactionId]
      })
    }, [deleteTransaction, transactionId, removeFromReport])

    const { title, entityName, isExpense } = useMemo(() => {
      const isExpense =
        (transaction?.amount ?? 0) < 0 ||
        transactionId === 'new-id-expense' ||
        view === 'team'
      return {
        isExpense,
        title: `${isNewTransaction ? 'New' : ''} ${
          isExpense ? 'Expense' : 'Revenue'
        } ${!isNewTransaction ? 'Details' : ''}`,
        entityName: isExpense ? 'expense' : 'revenue'
      }
    }, [transaction?.amount, transactionId, view, isNewTransaction])

    const prepareImageData = (data: FormValues, formData: FormData) => {
      if (data.photo_attributes === null && data.photo === null) {
        formData.append('transaction[photo_attributes][_destroy]', String(true))
      } else if (data.photo_attributes instanceof File) {
        const blob = new Blob([data.photo_attributes], {
          type: data.photo_attributes.type
        })
        const fileOfBlob = new File([blob], data.photo_attributes.name)
        formData.append('transaction[photo_attributes][image]', fileOfBlob)
      }
    }

    const prepareTagsData = (data: FormValues, formData: FormData) => {
      if (data.tags?.length) {
        data.tags.forEach((val) => {
          formData.append(`transaction[tags][]`, String(val))
        })
      }
      if (!data.tags?.length) {
        formData.append(`transaction[tags][]`, '')
      }
    }

    const onSubmit = useCallback(
      async (data: FormValues) => {
        if (!data.date) {
          setError(
            'date',
            { message: 'Date is required' },
            { shouldFocus: true }
          )
          notifications.error('Date is required')
          return
        }
        const formData = new FormData()
        const keys = Object.keys(data).filter(
          (key) =>
            ![
              'amount',
              'photo_attributes',
              'photo',
              'tags',
              'purpose'
            ].includes(key)
        )

        if (
          !data.report_token_id &&
          !isNewTransaction &&
          transaction?.report?.token_id
        ) {
          await removeFromReport(transactionId)
        }

        if (customIncomeSources.includes(data.purpose)) {
          formData.append('transaction[income_source]', data.purpose)
          formData.append('transaction[purpose]', 'Work')
        } else {
          // @ts-ignore
          formData.append('transaction[income_source]', null)
          formData.append('transaction[purpose]', data.purpose)
        }

        if (!data.notes) {
          formData.append('transaction[notes]', '')
        }

        const amount = parseFloat(data.amount?.replaceAll(/(\$|,)/g, ''))
        if (!isNaN(amount)) {
          formData.append(
            'transaction[amount]',
            String(isExpense ? amount * -1 : amount)
          )
        }

        prepareImageData(data, formData)
        prepareTagsData(data, formData)

        for (const key of keys) {
          const item = data[key as keyof FormValues]
          if (item) {
            // Only assign properties with values
            formData.append('transaction[' + key + ']', String(item))
          }
        }

        isNewTransaction
          ? await createTransaction(formData)
          : await updateTransaction(formData)
      },
      [
        isNewTransaction,
        transaction?.report?.token_id,
        customIncomeSources,
        createTransaction,
        updateTransaction,
        setError,
        notifications,
        removeFromReport,
        transactionId,
        isExpense
      ]
    )

    const reportOptions = useMemo(() => {
      const allReports = [
        {
          token_id: null,
          name: 'None',
          status_color: 'EBEBEB',
          status_text_color: '73767D',
          status: 'rejected',
          status_formatted: 'rejected'
        },
        ...getUniqueListBy<ReportTiny>(
          [
            ...[transaction?.report ?? []],
            ...(filteredReports ?? [])
          ] as ReportTiny[],
          'token_id'
        )
      ]

      return allReports?.length
        ? (allReports.map(
            ({
              token_id,
              name,
              status_color,
              status_text_color,
              status_formatted
            }) => {
              return {
                token_id,
                label: (
                  <span className="flex items-center justify-between w-full h-[1.3rem]">
                    <span className="-ml-2">{name ?? 'Un-named report'}</span>
                    {token_id && (
                      <Status
                        bgColor={status_color}
                        textColor={status_text_color}
                        status={status_formatted}
                      />
                    )}
                  </span>
                )
              }
            }
          ) as MenuProps<ReportTiny>['options'])
        : [
            {
              token_id: 'empty',
              label: 'No reports yet. Add a report to get reimbursed easily',
              disabled: true
            }
          ]
    }, [filteredReports, transaction?.report])

    useEffect(() => {
      if (isDirty || !transaction) return
      reset({
        report_token_id: transaction?.report
          ? transaction?.report?.token_id
          : null,
        notes: transaction.notes,
        amount: numeral(Math.abs(transaction.amount)).format('$0,0.00'),
        date: transaction.date,
        merchant: transaction.merchant,
        category: transaction.category,
        tags: transaction.tags,
        photo: transaction.photo?.token_id ? transaction.photo : null,
        photo_attributes: null,
        purpose:
          transaction.purpose === null && transaction.income_source === null
            ? 'Unclassified'
            : transaction.income_source ?? transaction.purpose
      })
    }, [isDirty, reset, transaction])

    const todayInRange = useMemo(() => {
      if (!enableStartDate || !enableEndDate) return true
      return (
        enableStartDate &&
        enableEndDate &&
        moment().isBetween(moment(enableStartDate), moment(enableEndDate))
      )
    }, [enableEndDate, enableStartDate])

    useEffect(() => {
      if (isNewTransaction) {
        setValue(
          'purpose',
          isExpense ? teamPurposes[0] ?? 'Personal' : 'Personal'
        )
        setValue(
          'date',
          !todayInRange
            ? moment(enableStartDate).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD')
        )
        setValue('tags', [])
      }
    }, [
      isNewTransaction,
      isExpense,
      setValue,
      teamPurposes,
      todayInRange,
      enableStartDate
    ])

    return isLoading ? (
      <div className="flex justify-center gap-5 items-center w-full h-screen text-center">
        <Icon name="loading" size={24} noWrapper />
        <p>Loading the {entityName}</p>
      </div>
    ) : (
      <div>
        {/*Locked banner*/}

        <div
          className={cn([
            'hidden mb-5 bg-dark-8 items-center gap-3 py-3 px-5 w-full',
            !!transaction?.is_locked && view === 'personal' && '!flex'
          ])}
        >
          <div>
            <LockFilledIcon color="#555555" height="25" width="25" />
          </div>
          <p>
            This {entityName} cannot be edited because it is either under
            review, approved, or paid. Contact your Team Manager to edit it.
          </p>
        </div>
        <div className="pb-20 px-6 pt-4">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between w-full">
              <p className="text-dark text-2xl font-semibold">{title}</p>
              <div className={cn(['flex gap-3', isReadOnly && 'hidden'])}>
                <Button
                  variant="primary"
                  type="submit"
                  status={
                    updatingTransaction || creatingTransaction
                      ? 'loading'
                      : 'idle'
                  }
                  disabled={!isDirty}
                >
                  Save
                </Button>
                {!isNewTransaction && (
                  <PopConfirm
                    onConfirm={onDelete}
                    title="Are you sure you want to delete this transaction?"
                  >
                    <Button
                      variant="outline"
                      icon="trash-outline"
                      className="text-danger"
                      status={deletingTransaction ? 'loading' : 'idle'}
                    />
                  </PopConfirm>
                )}
              </div>
            </div>
          </form>

          {/*Value and attachment*/}
          <div className="grid grid-cols-12 items-center py-10 gap-5 border-b border-dark-2">
            <div className="w-full col-span-8 h-full flex items-center justify-center border-r border-dark-1">
              {isReadOnly ? (
                <span
                  className={cn([
                    ' font-semibold',
                    amount?.toString().length > 8 ? 'text-4xl' : 'text-5xl',
                    amountNumber < 0 && 'text-danger'
                  ])}
                >
                  {numeral(amount).format('$0,0.00')}
                </span>
              ) : (
                <SmartField
                  controllerProps={{
                    control,
                    name: 'amount',
                    rules: {
                      required: 'Specify the amount',
                      validate: (value) => {
                        return value !== '$0.00' || `Specify the amount`
                      }
                    }
                  }}
                  label=""
                >
                  <Input
                    className={cn([
                      '!outline-none bg-transparent xs:text-4xl lg:text-4xl font-semibold',
                      'w-full ml:input-sm',
                      amountNumber < 0 && 'text-danger'
                    ])}
                    type="money"
                    placeholder="$0.00"
                    disabled={isReadOnly}
                  />
                </SmartField>
              )}
            </div>
            <div className="col-span-4">
              <FilePicker
                onChange={(file) => {
                  setValue('photo', null)
                  setValue('photo_attributes', file, {
                    shouldDirty: true
                  })
                }}
                value={photo}
                disabled={isReadOnly}
              />
            </div>
          </div>
          {/*Form*/}
          <div className="mt-10 flex flex-col gap-5">
            <SmartField
              controllerProps={{
                control,
                name: 'purpose',
                rules: {
                  required: 'Select a purpose',
                  validate: (value) => {
                    if (
                      hasTeam &&
                      !!report_token_id &&
                      !teamPurposes.includes(value as string)
                    ) {
                      notifications.error('Please select a work purpose')
                      return 'Please select a work purpose'
                    }
                  }
                }
              }}
              orientation="horizontal"
              label={
                <Icon
                  name="briefcase-outline"
                  size={25}
                  color="text-dark-4"
                  noWrapper
                />
              }
              requirementDecoration={null}
            >
              <Select
                placeholder="Choose purpose"
                readOnly={isReadOnly}
                variant="invisible"
                className="w-full"
                options={isPersonalReportPage ? teamPurposes : allPurposes}
              />
            </SmartField>

            <SmartField
              controllerProps={{
                control,
                name: 'merchant',
                rules: {
                  required: 'Specify the merchant'
                }
              }}
              orientation="horizontal"
              label={
                <Icon
                  name="shop-outline"
                  size={25}
                  color="text-dark-4"
                  noWrapper
                />
              }
              requirementDecoration={null}
            >
              <Input
                placeholder="Merchant"
                readOnly={isReadOnly}
                className="w-full"
                variant="invisible"
              />
            </SmartField>

            <SmartField
              controllerProps={{
                control,
                name: 'date',
                rules: {
                  validate: {
                    inRange: (value) => {
                      if (!enableStartDate && !enableEndDate) return true
                      return (
                        moment(value as string).isBetween(
                          moment(enableStartDate),
                          moment(enableEndDate),
                          'days',
                          '[]'
                        ) || 'Date is outside of the report period'
                      )
                    }
                  }
                }
              }}
              label={
                <Icon
                  name="calendar-outline"
                  size={25}
                  color="text-dark-4"
                  noWrapper
                />
              }
              orientation="horizontal"
            >
              <DatePicker
                type="date-picker"
                variant="invisible"
                className="flex w-full"
                hideIcon={true}
                disableToday={!todayInRange}
                disableAfter={enableEndDate ? moment(enableEndDate) : null}
                disableBefore={enableStartDate ? moment(enableStartDate) : null}
                disabled={isReadOnly}
                readOnly={isReadOnly}
              />
            </SmartField>

            {categoryInputConfig.show && (
              <Tooltip
                dark
                position="left-middle"
                triggerMode="click"
                dismissOnMouseLeave
                disabled={!categoryInputConfig.disabled}
                content={
                  <div className="w-56">
                    Categories are no longer enabled on your team
                  </div>
                }
              >
                <SmartField
                  controllerProps={{
                    control,
                    name: 'category'
                  }}
                  orientation="horizontal"
                  label={
                    <Icon
                      name="categories-outline"
                      size={25}
                      color="text-dark-4"
                      noWrapper
                    />
                  }
                >
                  <CategoryOption
                    view={view}
                    disabled={isReadOnly || categoryInputConfig.disabled}
                    readOnly={isReadOnly || categoryInputConfig.disabled}
                  />
                </SmartField>
              </Tooltip>
            )}
            <SmartField
              controllerProps={{
                control,
                name: 'notes'
              }}
              orientation="horizontal"
              label={
                <Icon
                  name="note-outline"
                  size={25}
                  color="text-dark-4"
                  noWrapper
                />
              }
            >
              <Input
                placeholder={isReadOnly ? 'No notes added' : 'Add a note...'}
                readOnly={isReadOnly}
                variant="invisible"
                className="w-full"
              />
            </SmartField>

            {isExpense && hasTeam && (
              <SmartField
                controllerProps={{
                  control,
                  name: 'report_token_id'
                }}
                orientation="horizontal"
                label={
                  <Icon
                    name="report-outline"
                    size={25}
                    color="text-dark-4"
                    noWrapper
                  />
                }
              >
                <Select
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                  placeholder="Choose report"
                  className={cn([
                    'w-full !pr-2',
                    isReadOnly
                      ? 'text-dark'
                      : '!outline-0 hover:!outline-1 hover:!outline-primary'
                  ])}
                  options={reportOptions}
                  variant="invisible"
                />
              </SmartField>
            )}

            {tagsInputConfig.show && (
              <Controller
                name="tags"
                control={control}
                render={({ field }) => {
                  return (
                    <Tooltip
                      dark
                      position="left-middle"
                      triggerMode="click"
                      dismissOnMouseLeave
                      disabled={!tagsInputConfig.disabled}
                      content={
                        <div className="w-56">
                          Tags are no longer enabled on your team
                        </div>
                      }
                    >
                      <Field
                        orientation="horizontal"
                        label={
                          <Icon
                            name="tag-outline"
                            size={25}
                            color="text-dark-4"
                            noWrapper
                          />
                        }
                      >
                        <Autosuggest
                          value={field.value}
                          onChange={field.onChange}
                          emptySlot={
                            isLoadingTags ? (
                              <div className="card bg-white flex flex-col justify-center items-center gap-2">
                                <p className="text-sm text-center">
                                  Loading...
                                </p>
                              </div>
                            ) : tagQuery && !hasTeam ? (
                              <div className="card bg-white flex flex-col justify-center items-center gap-2">
                                <Button
                                  icon="plus"
                                  onClick={() => {
                                    setValue(
                                      'tags',
                                      Array.from(
                                        new Set([...field.value, ...[tagQuery]])
                                      )
                                    )
                                  }}
                                >
                                  Add tag {tagQuery}
                                </Button>
                                <p className="text-sm text-center">
                                  Search results
                                </p>
                              </div>
                            ) : undefined
                          }
                          name="tags"
                          className={cn([
                            'w-full !outline-0 pl-0',
                            !isReadOnly &&
                              !tagsInputConfig.disabled &&
                              'hover:!outline-1 cursor-pointer'
                          ])}
                          placeholder={
                            field?.value?.length
                              ? ''
                              : isReadOnly
                                ? 'No tags added'
                                : 'Add tags'
                          }
                          readOnly={isReadOnly || tagsInputConfig.disabled}
                          variant="invisible"
                          onInputChange={(value) =>
                            setTagQuery(value as string)
                          }
                          useTagOptions
                          options={tags?.map((t) => t.name) ?? []}
                          status={isLoadingTags ? 'loading' : 'idle'}
                        />
                      </Field>
                    </Tooltip>
                  )
                }}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
)

export default TransactionDetail
